import { useEffect, useMemo, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useLazyQuery } from '@apollo/client/react';

import { GET_SEARCH_RESULTS } from '@atom/graph/search';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { List, Progress } from '@atom/mui';
import { SearchResultsType } from '@atom/types/search';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import AllResults from './AllResults';
import InventoryResultRow from './InventoryResultRow';
import SearchPortalHeader from './SearchPortalHeader';
import UserResultRow from './UserResultRow';
import WorkResultRow from './WorkResultRow';

import classes from './searchPortal.module.css';

const styles = {
  list: {
    height: '100%',
    padding: 0,
    margin: 0,
  },
  virtuoso: {
    height: '100%',
  },
  progress: {
    marginTop: '5rem',
  },
};

export enum View {
  ALL = 'ALL',
  INVENTORY = 'INVENTORY',
  WORK = 'WORK',
  USERS = 'USERS',
}

const SearchPortal = () => {
  const [{ query }] = useNavigation({
    query: SearchParamType.STRING,
  });

  const [results, setResults] = useState<SearchResultsType>();
  const [activeView, setActiveView] = useState<View>(View.ALL);

  const [getSearch, { loading }] = useLazyQuery(GET_SEARCH_RESULTS, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    const getSearchRequest = async () => {
      const { data } = await getSearch({
        variables: {
          input: {
            query,
            top: false,
            showAdmin: hasRolePermissions(ROLE_SETS.ADMIN),
            includeAssets:
              activeView === View.INVENTORY || activeView === View.ALL,
            includeWorkOrders:
              activeView === View.WORK || activeView === View.ALL,
            includeUsers: activeView === View.USERS || activeView === View.ALL,
          },
        },
      });
      setResults(data?.search);
    };

    getSearchRequest();
  }, [query, activeView]);

  const content = useMemo(() => {
    const views = {
      [View.ALL]: <AllResults query={query} results={results} />,
      [View.INVENTORY]: (
        <Virtuoso
          style={styles.virtuoso}
          data={results?.assets}
          itemContent={(_, item) => (
            <InventoryResultRow query={query} asset={item} />
          )}
        />
      ),
      [View.WORK]: (
        <Virtuoso
          style={styles.virtuoso}
          data={results?.workOrders}
          itemContent={(_, item) => <WorkResultRow query={query} work={item} />}
        />
      ),
      [View.USERS]: (
        <Virtuoso
          style={styles.virtuoso}
          data={results?.users}
          itemContent={(_, item) => <UserResultRow query={query} user={item} />}
        />
      ),
    };

    return views[activeView];
  }, [loading, results, activeView]);

  const searchTotal = useMemo(() => {
    if (loading || isNilOrEmpty(results)) {
      return 0;
    }

    const { users, assets, workOrders } = results;

    switch (activeView) {
      case View.ALL: {
        return users.length + assets.length + workOrders.length;
      }
      case View.INVENTORY: {
        return assets.length;
      }
      case View.USERS: {
        return users.length;
      }
      case View.WORK: {
        return workOrders.length;
      }
      default: {
        return 0;
      }
    }
  }, [loading, results, activeView]);

  const searchTotalText =
    loading || isNilOrEmpty(results)
      ? 'Searching ...'
      : `${searchTotal} search results`;

  return (
    <div className={classes.portalContainer}>
      <SearchPortalHeader
        query={query}
        activeView={activeView}
        setActiveView={setActiveView}
      />
      <div className={classes.searchPortalResultsContainer}>
        <div className={classes.searchPortalResults}>
          <div className={classes.searchPortalResultsHeading}>
            {searchTotalText}
          </div>
          <div className={classes.contentContainer}>
            {loading ? (
              <div style={styles.progress}>
                <Progress />
              </div>
            ) : (
              <List style={styles.list}>{content}</List>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPortal;
